/**
 * Task Form
 *
 * Two-mode creation: add a flexible plan activity or create a custom task linked
 * to an active care plan.
 * OCPE Phase 4: Uses plan-backed activity and custom task creation modes.
 * Edit mode unchanged: displays existing task type and editable fields.
 */

// Components
import { Select, Skeleton, Stack, Textarea, Text, Alert } from "@mantine/core";
import {
  IconInfoCircle,
  IconX,
  IconListCheck,
  IconPencilPlus,
} from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormActions } from "@benchmark/ui/components/atomic";
import { CardSelect } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { PractitionerRoleSearchSelect } from "@benchmark/ui/components/atomic";

// Constants
import {
  CUSTOM_TASK_TYPE,
  resolveActivityDisplay,
} from "@benchmark/core/constants";
import { CONFIG } from "@/app/config";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum, useMedplumProfile, useResource } from "@medplum/react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import {
  usePatientSearch,
  useBenchmarkStaffSearch,
} from "@/hooks/shared/useFHIROptions";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";
import { usePatientFlexibleActivities } from "@/hooks/domain/usePatientFlexibleActivities";

// Utils
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createTaskFacade } from "@/facades/task.facade";
import { taskToForm } from "@benchmark/core/adapters";
import { validateTaskFormData } from "@benchmark/ui/validators";
import { normalizeDateInputValue } from "@benchmark/ui/utils";
import { isPast } from "@benchmark/ui/utils";
import { addCarePlanActivity } from "@benchmark/core/actions";

// Types
import type { Task, Patient, Practitioner } from "@medplum/fhirtypes";
import type { TaskFormData } from "@benchmark/core/types";

const TASK_MODE_OPTIONS = [
  {
    value: "plan-activity",
    label: "Add Plan Activity",
    description: "Add a flexible activity from this member's care plan",
    icon: <IconListCheck size={20} />,
  },
  {
    value: "custom",
    label: "Create Custom Task",
    description: "Create an ad-hoc standalone task for this member",
    icon: <IconPencilPlus size={20} />,
  },
];

type TaskFormProps = {
  patientId?: string;
  task?: Task;
  onSuccess?: (taskId: string) => void;
  onCancel?: () => void;
};

/**
 * Ref interface for task form (form protection).
 * @agentic connections
 */
export type TaskFormRef = {
  checkProtection: () => Promise<boolean>;
  markAsClean: () => void;
};

/**
 * Form for creating and editing FHIR Tasks with task type selection and assignment.
 * @agentic connections
 */
export const TaskForm = forwardRef<TaskFormRef, TaskFormProps>(
  function TaskForm(
    {
      patientId: propPatientId,
      task: propTask,
      onSuccess,
      onCancel,
    }: TaskFormProps = {},
    ref,
  ) {
    const medplum = useMedplum();
    const taskFacade = createTaskFacade(medplum);
    const navigate = useNavigate();
    const { taskId, patientId: urlPatientId } = useStableParams();

    const effectivePatientId = propPatientId || urlPatientId;
    const { data: patientResource } = usePatientResource(effectivePatientId);
    const patient = patientResource as Patient | undefined;

    const isInPatientContext = !!effectivePatientId;
    const profile = useMedplumProfile() as Practitioner;

    const fetchedTask = useResource(
      taskId ? { reference: `Task/${taskId}` } : undefined,
    );
    const existingTask = propTask || fetchedTask;

    const {
      practitionerRole: currentUserPractitionerRole,
      isLoading: isRoleLoading,
    } = useCurrentPractitionerRole();
    const [isLoading, setIsLoading] = useState(true);
    const [taskMode, setTaskMode] = useState<"plan-activity" | "custom">(
      "plan-activity",
    );

    const [patientSearchQuery, setPatientSearchQuery] = useState("");
    const [ownerSearchQuery, setOwnerSearchQuery] = useState("");

    const form = useForm<
      TaskFormData & {
        "plan-activity-code"?: string;
        "plan-care-plan-id"?: string;
      }
    >({
      initialValues: {
        "task-type": CUSTOM_TASK_TYPE.code,
        "custom-task-display": "",
        "patient-id": effectivePatientId || "",
        "plan-activity-code": "",
        "plan-care-plan-id": "",
        description: "",
        status: "ready",
        priority: "routine",
        intent: "plan",
        "assigned-to": "",
        requester: "",
        "authored-on": new Date().toISOString(),
        "due-date": "",
        "estimated-duration": undefined,
        "business-status": "",
        notes: "",
      },
      validate: (values) => {
        const errorMap: Record<string, string> = {};

        // Edit mode: validate only editable fields (task-type comes from existing task)
        if (existingTask) {
          if (!isInPatientContext && !values["patient-id"]) {
            errorMap["patient-id"] = "Member is required!";
          }
          if (!values["assigned-to"]) {
            errorMap["assigned-to"] = "Owner is required!";
          }
          if (!values.description?.trim()) {
            errorMap.description = "Description is required";
          }
          if (
            values["task-type"] === CUSTOM_TASK_TYPE.code &&
            !values["custom-task-display"]?.trim()
          ) {
            errorMap["custom-task-display"] = "Custom task name is required";
          }
          if (values["due-date"]) {
            if (isPast(values["due-date"])) {
              errorMap["due-date"] = "Due date cannot be in the past";
            }
          }
          return errorMap;
        }

        // Create mode
        if (!isInPatientContext && !values["patient-id"]) {
          errorMap["patient-id"] = "Member is required!";
        }

        if (!values["assigned-to"] && taskMode === "custom") {
          errorMap["assigned-to"] = "Owner is required!";
        }

        if (taskMode === "plan-activity") {
          if (!values["plan-activity-code"]) {
            errorMap["plan-activity-code"] = "Activity is required";
          }
        } else {
          const errors = validateTaskFormData({
            ...values,
            "task-type": CUSTOM_TASK_TYPE.code,
          });
          errors.forEach((error) => {
            if (error.includes("Task type")) {
              errorMap["task-type"] = error;
            } else if (error.includes("Description")) {
              errorMap.description = error;
            } else if (error.includes("Due date")) {
              errorMap["due-date"] = error;
            } else if (error.includes("Custom task name")) {
              errorMap["custom-task-display"] = error;
            } else {
              errorMap.general = error;
            }
          });
        }

        return errorMap;
      },
    });

    const {
      checkProtection,
      markAsClean,
      ConfirmationModal: TaskFormConfirmationModal,
    } = useFormProtection(form, {
      enabled: true,
      blockNavigation: true,
    });

    useImperativeHandle(
      ref,
      () => ({
        checkProtection,
        markAsClean,
      }),
      [checkProtection, markAsClean],
    );

    const patientIdForPlanActivities =
      effectivePatientId || (form.values["patient-id"] as string) || undefined;
    const {
      carePlan,
      flexibleActivityOptions,
      isLoading: isLoadingPlan,
      hasActiveCarePlan,
    } = usePatientFlexibleActivities(patientIdForPlanActivities);

    const { createSubmissionHandler, isSubmitting } = useFormSubmission();

    const {
      options: patientSearchOptions,
      isSearching: searchingPatients,
      resources: patientSearchResults,
    } = usePatientSearch(
      patientSearchQuery,
      {},
      { enabled: !effectivePatientId },
    );

    const {
      options: ownerSearchOptions,
      isSearching: searchingOwners,
      resources: ownerSearchResults,
    } = useBenchmarkStaffSearch(
      ownerSearchQuery,
      CONFIG.VITE_BENCHMARK_ORGANIZATION_ID,
    );

    const patientOptions = useMemo(() => {
      if (patientSearchQuery.trim().length >= 2) {
        return patientSearchOptions;
      }
      const selectedId = form.values["patient-id"] as string | undefined;
      const selectedLabel = form.values["patient-id-display"] as
        string | undefined;
      if (selectedId && selectedLabel) {
        return [{ value: selectedId, label: selectedLabel }];
      }
      return [];
    }, [patientSearchQuery, patientSearchOptions, form.values]);

    const practitionerRoleOptions = useMemo(() => {
      if (ownerSearchQuery.trim().length >= 2) {
        return ownerSearchOptions;
      }
      const selectedId = form.values["assigned-to"] as string | undefined;
      const selectedLabel = form.values["assigned-to-display"] as
        string | undefined;
      if (selectedId && selectedLabel) {
        return [{ value: selectedId, label: selectedLabel }];
      }
      return [];
    }, [ownerSearchQuery, ownerSearchOptions, form.values]);

    const planActivitySelectData = useMemo(
      () =>
        flexibleActivityOptions.map((a) => ({
          value: a.value,
          label: a.label,
          description: a.description,
        })),
      [flexibleActivityOptions],
    );

    const modeOptions = useMemo(() => {
      if (!hasActiveCarePlan && patientIdForPlanActivities) {
        return TASK_MODE_OPTIONS.map((opt) =>
          opt.value === "plan-activity"
            ? {
                ...opt,
                disabled: true,
                disabledTooltip:
                  "No active care plan. Enroll this member first.",
              }
            : opt,
        );
      }
      return [...TASK_MODE_OPTIONS];
    }, [hasActiveCarePlan, patientIdForPlanActivities]);

    useEffect(() => {
      if (!currentUserPractitionerRole?.id || existingTask) return;
      if (!form.values["assigned-to"]) {
        form.setFieldValue("assigned-to", currentUserPractitionerRole.id);
        form.setFieldValue(
          "assigned-to-display",
          currentUserPractitionerRole.practitioner?.display || "",
        );
      }
      form.setFieldValue("requester", currentUserPractitionerRole.id);
      form.setFieldValue(
        "requester-display",
        currentUserPractitionerRole.practitioner?.display || "",
      );
      form.resetDirty();
    }, [currentUserPractitionerRole?.id, existingTask]);

    const handlePatientSelect = useCallback(
      (value: string | null) => {
        form.setFieldValue("patient-id", value || "");
        if (value) {
          const selected = patientSearchResults.find((r) => r.id === value);
          if (selected) {
            const name = selected.name?.[0];
            const displayName = name
              ? `${name.given?.join(" ") || ""} ${name.family || ""}`.trim()
              : selected.id || "Unknown";
            form.setFieldValue("patient-id-display", displayName);
          }
        } else {
          form.setFieldValue("patient-id-display", "");
        }
      },
      [form, patientSearchResults],
    );

    const handleOwnerSelect = useCallback(
      (value: string | null) => {
        form.setFieldValue("assigned-to", value || "");
        if (value) {
          const selected = ownerSearchResults.find((r) => r.id === value);
          if (selected) {
            form.setFieldValue(
              "assigned-to-display",
              selected.practitioner?.display ||
                selected.code?.[0]?.coding?.[0]?.display ||
                "",
            );
          }
        } else {
          form.setFieldValue("assigned-to-display", "");
        }
      },
      [form, ownerSearchResults],
    );

    const handleModeChange = useCallback(
      (value: string | string[] | null) => {
        const raw = Array.isArray(value) ? value[0] : value;
        const mode =
          raw === "plan-activity" || raw === "custom" ? raw : "plan-activity";
        setTaskMode(mode);
        if (mode === "plan-activity") {
          form.setFieldValue("plan-activity-code", "");
          form.setFieldValue("plan-care-plan-id", "");
          form.setFieldValue("task-type", undefined);
          form.setFieldValue("custom-task-display", "");
          form.setFieldValue("description", "");
        } else {
          form.setFieldValue("task-type", CUSTOM_TASK_TYPE.code);
          form.setFieldValue("plan-activity-code", "");
          form.setFieldValue("plan-care-plan-id", "");
        }
      },
      [form],
    );

    const handlePlanActivityChange = useCallback(
      (value: string | null) => {
        form.setFieldValue("plan-activity-code", value || "");
        form.setFieldValue("plan-care-plan-id", carePlan?.id || "");
      },
      [form, carePlan?.id],
    );

    useEffect(() => {
      async function loadInitialData() {
        if (!profile) return;
        if (isInPatientContext && !patient) return;

        try {
          setIsLoading(true);
          if (!existingTask) {
            setIsLoading(false);
            return;
          }

          const data = taskToForm(existingTask as Task);
          form.setValues(data);
          form.resetDirty(data);
        } catch (error) {
          console.error("Error loading initial data:", error);
          showErrorNotification(
            "Failed to load task data. Please refresh and try again.",
          );
        } finally {
          setIsLoading(false);
        }
      }
      loadInitialData();
    }, [existingTask, patient, profile, isInPatientContext]);

    const handleSubmit = useCallback(
      createSubmissionHandler(
        async (
          formData: TaskFormData & {
            "plan-activity-code"?: string;
            "plan-care-plan-id"?: string;
          },
        ) => {
          if (!profile || !currentUserPractitionerRole) {
            showErrorNotification(
              "User is missing a valid Benchmark PractitionerRole. Please notify engineering.",
            );
            return;
          }

          let targetPatient = patient;
          if (!targetPatient && formData["patient-id"]) {
            try {
              targetPatient = await medplum.readResource(
                "Patient",
                formData["patient-id"],
              );
            } catch {
              showErrorNotification(
                "Failed to load selected patient. Please try again.",
              );
              return;
            }
          }

          if (!targetPatient) {
            showErrorNotification("Member is required to create a task.");
            return;
          }

          try {
            // Plan-activity path is for creating new activities only; edit uses upsert
            if (taskMode === "plan-activity" && !existingTask) {
              const activityCode = formData["plan-activity-code"];
              const carePlanId = formData["plan-care-plan-id"] || carePlan?.id;
              if (!activityCode || !carePlanId) {
                showErrorNotification("Activity and care plan are required.");
                return;
              }
              const planToUse =
                carePlan?.id === carePlanId
                  ? carePlan
                  : await medplum.readResource("CarePlan", carePlanId);
              const ownerOverride =
                formData["assigned-to"] && formData["assigned-to-display"]
                  ? {
                      ref: formData["assigned-to"],
                      display: formData["assigned-to-display"],
                    }
                  : undefined;
              const updated = await addCarePlanActivity(medplum, {
                carePlan: planToUse,
                activityCode,
                targetDate: formData["due-date"] || undefined,
                description: formData.description || undefined,
                ownerOverride,
              });
              const lastActivity = updated.activity?.slice(-1)[0];
              const taskRef = lastActivity?.reference?.reference;
              const taskIdFromRef = taskRef?.startsWith("Task/")
                ? taskRef.replace("Task/", "")
                : undefined;
              markAsClean();
              if (taskIdFromRef) {
                showSuccessNotification("Plan activity added successfully.");
                if (onSuccess) {
                  onSuccess(taskIdFromRef);
                } else {
                  navigate(
                    `/Patient/${targetPatient.id}/Task/${taskIdFromRef}`,
                  )?.catch(console.error);
                }
              } else {
                showSuccessNotification("Plan activity added successfully.");
                if (onSuccess) onSuccess(updated.id!);
                else
                  navigate(`/Patient/${targetPatient.id}`)?.catch(
                    console.error,
                  );
              }
              return;
            }

            if (formData["assigned-to"] && !formData["assigned-to-display"]) {
              const selected = ownerSearchResults.find(
                (r) => r.id === formData["assigned-to"],
              );
              if (selected) {
                formData["assigned-to-display"] =
                  selected.practitioner?.display ||
                  selected.code?.[0]?.coding?.[0]?.display ||
                  "";
              } else {
                showErrorNotification(
                  "Selected practitioner role does not have a display name.",
                );
                return;
              }
            }

            if (taskMode === "custom" && !existingTask) {
              const submissionData: TaskFormData = {
                ...formData,
                "task-type": CUSTOM_TASK_TYPE.code,
                status: "ready",
                intent: "plan",
                requester: currentUserPractitionerRole.id,
                "requester-display":
                  formData["requester-display"] ||
                  currentUserPractitionerRole.practitioner?.display ||
                  "",
                "assigned-to-display": formData["assigned-to-display"] || "",
                "authored-on": new Date().toISOString(),
              };

              const customTask = await taskFacade.upsert(
                submissionData,
                targetPatient,
                undefined,
              );

              if (!customTask.id) {
                showErrorNotification(
                  "Custom task was created without an ID. Please try again.",
                );
                return;
              }

              showSuccessNotification("Custom task created successfully.");
              markAsClean();

              if (onSuccess) {
                onSuccess(customTask.id);
              } else {
                navigate(
                  `/Patient/${targetPatient.id}/Task/${customTask.id}`,
                )?.catch(console.error);
              }
              return;
            }

            const isEditing = !!existingTask;
            const editBaseline = isEditing
              ? taskToForm(existingTask as Task)
              : undefined;
            const preservedRequester = editBaseline?.requester;
            const preservedRequesterDisplay =
              editBaseline?.["requester-display"];
            const submissionData: TaskFormData = {
              ...formData,
              "task-type":
                isEditing && editBaseline
                  ? formData["task-type"] &&
                    formData["task-type"] !== CUSTOM_TASK_TYPE.code
                    ? formData["task-type"]
                    : (editBaseline["task-type"] ?? CUSTOM_TASK_TYPE.code)
                  : CUSTOM_TASK_TYPE.code,
              status: isEditing ? formData.status || "ready" : "ready",
              intent: isEditing ? formData.intent || "order" : "plan",
              requester: isEditing
                ? preservedRequester
                : currentUserPractitionerRole.id,
              "requester-display": isEditing
                ? preservedRequesterDisplay
                : formData["requester-display"] ||
                  currentUserPractitionerRole.practitioner?.display ||
                  "",
              "assigned-to-display": formData["assigned-to-display"] || "",
              "authored-on": isEditing
                ? formData["authored-on"] || new Date().toISOString()
                : new Date().toISOString(),
            };

            const task = await taskFacade.upsert(
              submissionData,
              targetPatient,
              existingTask as Task | undefined,
            );

            showSuccessNotification(
              `Task ${existingTask ? "updated" : "created"} successfully.`,
            );
            markAsClean();

            if (onSuccess && task.id) {
              onSuccess(task.id);
            } else {
              if (isInPatientContext) {
                navigate(`/Patient/${targetPatient.id}/Task/${task.id}`)?.catch(
                  console.error,
                );
              } else {
                navigate("/Task")?.catch(console.error);
              }
            }
          } catch (error) {
            console.error("Error saving task:", error);
            showErrorNotification(
              `Failed to ${existingTask ? "update" : "create"} task. Please try again.`,
            );
          }
        },
      ),
      [
        profile,
        patient,
        currentUserPractitionerRole,
        medplum,
        navigate,
        existingTask,
        taskMode,
        carePlan,
        createSubmissionHandler,
        onSuccess,
        markAsClean,
        isInPatientContext,
        ownerSearchResults,
      ],
    );

    if (!profile || isRoleLoading || (isInPatientContext && !patient)) {
      return <Skeleton height={200} />;
    }

    if (isLoading) {
      return <Skeleton height={200} />;
    }

    const isEditMode = !!existingTask;

    const getError = (key: string): string | undefined => {
      const err = form.errors[key];
      return typeof err === "string" ? err : undefined;
    };

    if (isEditMode) {
      const taskCode = (existingTask as Task).code?.coding?.[0]?.code;
      const taskTypeDisplay = taskCode
        ? resolveActivityDisplay(taskCode)
        : "Task";
      const isCustomTask = form.values["task-type"] === CUSTOM_TASK_TYPE.code;

      return (
        <form
          onSubmit={form.onSubmit(handleSubmit, () => {
            showErrorNotification("Please fix form errors before submitting.");
          })}
          aria-label="Edit task form"
        >
          <Stack gap="md">
            <Alert
              icon={<IconInfoCircle size={16} />}
              color="gray"
              variant="light"
            >
              <Text size="sm" fw={500}>
                {taskTypeDisplay}
              </Text>
            </Alert>

            {isCustomTask && (
              <FormField
                label="Custom Task Name"
                required
                error={getError("custom-task-display")}
              >
                <TextInput
                  value={form.values["custom-task-display"] || ""}
                  onChange={(e) =>
                    form.setFieldValue(
                      "custom-task-display",
                      e.currentTarget.value,
                    )
                  }
                  data-testid="custom-task-name-input"
                />
              </FormField>
            )}

            {!isInPatientContext && (
              <FormField label="Member" required>
                <Select
                  placeholder="Search members..."
                  value={form.values["patient-id"] || ""}
                  onChange={handlePatientSelect}
                  onSearchChange={setPatientSearchQuery}
                  data={patientOptions}
                  searchable
                  clearable
                  filter={({ options }) => options}
                  disabled
                  nothingFoundMessage="Type at least 2 characters to search"
                />
              </FormField>
            )}

            <FormField label="Due Date" error={getError("due-date")}>
              <TextInput
                type="date"
                value={
                  form.values["due-date"]
                    ? (() => {
                        const v = form.values["due-date"] as string;
                        return /^\d{4}-\d{2}-\d{2}$/.test(v)
                          ? v
                          : normalizeDateInputValue(v);
                      })()
                    : ""
                }
                onChange={(e) =>
                  form.setFieldValue("due-date", e.currentTarget.value || "")
                }
                rightSection={
                  form.values["due-date"] ? (
                    <IconX
                      size={16}
                      style={{ cursor: "pointer" }}
                      onClick={() => form.setFieldValue("due-date", "")}
                    />
                  ) : undefined
                }
              />
            </FormField>

            <FormField
              label="Description"
              required
              error={getError("description")}
            >
              <Textarea
                value={form.values.description || ""}
                onChange={(e) =>
                  form.setFieldValue("description", e.target.value)
                }
                placeholder="Describe what needs to be done..."
                autosize
                minRows={3}
              />
            </FormField>

            <FormField label="Owner" required error={getError("assigned-to")}>
              <PractitionerRoleSearchSelect
                options={practitionerRoleOptions}
                value={(form.values["assigned-to"] as string) || null}
                searchQuery={ownerSearchQuery}
                isSearching={searchingOwners}
                onChange={handleOwnerSelect}
                onSearchChange={setOwnerSearchQuery}
                placeholder="Search practitioners..."
                noResultsMessage="No practitioners found"
              />
            </FormField>

            <FormActions
              isSubmitting={isSubmitting}
              resourceType="Task"
              isEditing={true}
              showCancel={true}
              onCancel={
                onCancel ||
                (() => (isInPatientContext ? navigate(-1) : navigate("/Task")))
              }
            />
          </Stack>
          <TaskFormConfirmationModal />
        </form>
      );
    }

    return (
      <form
        onSubmit={form.onSubmit(handleSubmit, () => {
          showErrorNotification("Please fix form errors before submitting.");
        })}
        aria-label="Create task form"
      >
        <Stack gap="md">
          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            <Text size="sm">
              Care plan activities are linked to the member&apos;s active care
              plan. Custom tasks are standalone and are not linked to care
              plans.
            </Text>
          </Alert>

          <FormField label="Task Type" required>
            <CardSelect
              value={taskMode}
              onChange={handleModeChange}
              data={modeOptions}
              columns={2}
              aria-label="Task type"
              data-testid="task-mode-select"
            />
          </FormField>

          {taskMode === "plan-activity" && (
            <Stack gap="sm">
              {!hasActiveCarePlan && patientIdForPlanActivities && (
                <Alert
                  icon={<IconInfoCircle size={16} />}
                  color="blue"
                  variant="light"
                  title="No active care plan"
                >
                  <Text size="sm">
                    Enroll the member in a care plan (intake, triage, or CoCM)
                    to add plan activities.
                  </Text>
                </Alert>
              )}

              {!isInPatientContext && (
                <FormField
                  label="Member"
                  required
                  error={getError("patient-id")}
                >
                  <Select
                    placeholder={
                      patientSearchQuery.trim().length < 2
                        ? "Type at least 2 characters to search"
                        : searchingPatients
                          ? "Searching..."
                          : "Search members..."
                    }
                    value={form.values["patient-id"] || ""}
                    onChange={handlePatientSelect}
                    onSearchChange={setPatientSearchQuery}
                    data={patientOptions}
                    searchable
                    clearable
                    filter={({ options }) => options}
                    nothingFoundMessage={
                      patientSearchQuery.trim().length < 2
                        ? "Type at least 2 characters to search"
                        : "No members found"
                    }
                  />
                </FormField>
              )}

              {hasActiveCarePlan && (
                <>
                  <FormField
                    label="Activity"
                    required
                    error={getError("plan-activity-code")}
                  >
                    <CardSelect
                      value={form.values["plan-activity-code"] || null}
                      onChange={(value) => {
                        const raw = Array.isArray(value) ? value[0] : value;
                        handlePlanActivityChange(raw ?? null);
                      }}
                      data={planActivitySelectData}
                      columns={1}
                      compact
                      aria-label="Select activity"
                      disabled={isLoadingPlan}
                    />
                  </FormField>

                  {form.values["plan-activity-code"] && (
                    <>
                      <FormField label="Due Date" error={getError("due-date")}>
                        <TextInput
                          type="date"
                          value={
                            form.values["due-date"]
                              ? (() => {
                                  const v = form.values["due-date"] as string;
                                  return /^\d{4}-\d{2}-\d{2}$/.test(v)
                                    ? v
                                    : normalizeDateInputValue(v);
                                })()
                              : ""
                          }
                          onChange={(e) =>
                            form.setFieldValue(
                              "due-date",
                              e.currentTarget.value || "",
                            )
                          }
                          rightSection={
                            form.values["due-date"] ? (
                              <IconX
                                size={16}
                                style={{ cursor: "pointer" }}
                                onClick={() =>
                                  form.setFieldValue("due-date", "")
                                }
                              />
                            ) : undefined
                          }
                        />
                      </FormField>

                      <FormField
                        label="Description"
                        error={getError("description")}
                      >
                        <Textarea
                          value={form.values.description || ""}
                          onChange={(e) =>
                            form.setFieldValue("description", e.target.value)
                          }
                          placeholder="Add details or instructions for this activity..."
                          autosize
                          minRows={2}
                          maxRows={4}
                        />
                      </FormField>

                      <FormField label="Owner" error={getError("assigned-to")}>
                        <PractitionerRoleSearchSelect
                          options={practitionerRoleOptions}
                          value={(form.values["assigned-to"] as string) || null}
                          searchQuery={ownerSearchQuery}
                          isSearching={searchingOwners}
                          onChange={handleOwnerSelect}
                          onSearchChange={setOwnerSearchQuery}
                          placeholder="Search practitioners..."
                          noResultsMessage="No practitioners found"
                        />
                      </FormField>
                    </>
                  )}

                  <FormActions
                    isSubmitting={isSubmitting}
                    resourceType="Task"
                    isEditing={false}
                    showCancel={true}
                    onCancel={
                      onCancel ||
                      (() =>
                        isInPatientContext ? navigate(-1) : navigate("/Task"))
                    }
                  />
                </>
              )}
            </Stack>
          )}

          {taskMode === "custom" && (
            <>
              {!isInPatientContext && (
                <FormField
                  label="Member"
                  required
                  error={getError("patient-id")}
                >
                  <Select
                    placeholder={
                      patientSearchQuery.trim().length < 2
                        ? "Type at least 2 characters to search"
                        : searchingPatients
                          ? "Searching..."
                          : "Search members..."
                    }
                    value={form.values["patient-id"] || ""}
                    onChange={handlePatientSelect}
                    onSearchChange={setPatientSearchQuery}
                    data={patientOptions}
                    searchable
                    clearable
                    filter={({ options }) => options}
                    nothingFoundMessage={
                      patientSearchQuery.trim().length < 2
                        ? "Type at least 2 characters to search"
                        : "No members found"
                    }
                  />
                </FormField>
              )}

              <Alert
                icon={<IconInfoCircle size={16} />}
                color="gray"
                variant="light"
              >
                <Text size="sm">
                  Custom tasks are standalone and not care-plan linked.
                </Text>
              </Alert>

              <FormField
                label="Custom Task Name"
                required
                error={getError("custom-task-display")}
              >
                <TextInput
                  value={form.values["custom-task-display"] || ""}
                  onChange={(e) =>
                    form.setFieldValue(
                      "custom-task-display",
                      e.currentTarget.value,
                    )
                  }
                  placeholder="e.g. Follow up on lab results"
                  data-testid="custom-task-name-input"
                />
              </FormField>

              <FormField label="Due Date" error={getError("due-date")}>
                <TextInput
                  type="date"
                  value={
                    form.values["due-date"]
                      ? (() => {
                          const v = form.values["due-date"] as string;
                          return /^\d{4}-\d{2}-\d{2}$/.test(v)
                            ? v
                            : normalizeDateInputValue(v);
                        })()
                      : ""
                  }
                  onChange={(e) =>
                    form.setFieldValue("due-date", e.currentTarget.value || "")
                  }
                  rightSection={
                    form.values["due-date"] ? (
                      <IconX
                        size={16}
                        style={{ cursor: "pointer" }}
                        onClick={() => form.setFieldValue("due-date", "")}
                      />
                    ) : undefined
                  }
                />
              </FormField>

              <FormField
                label="Description"
                required
                error={getError("description")}
              >
                <Textarea
                  value={form.values.description || ""}
                  onChange={(e) =>
                    form.setFieldValue("description", e.target.value)
                  }
                  placeholder="Describe what needs to be done..."
                  autosize
                  minRows={3}
                />
              </FormField>

              <FormField label="Owner" required error={getError("assigned-to")}>
                <PractitionerRoleSearchSelect
                  options={practitionerRoleOptions}
                  value={(form.values["assigned-to"] as string) || null}
                  searchQuery={ownerSearchQuery}
                  isSearching={searchingOwners}
                  onChange={handleOwnerSelect}
                  onSearchChange={setOwnerSearchQuery}
                  placeholder="Search practitioners..."
                  noResultsMessage="No practitioners found"
                />
              </FormField>

              <FormActions
                isSubmitting={isSubmitting}
                resourceType="Task"
                isEditing={false}
                showCancel={true}
                onCancel={
                  onCancel ||
                  (() =>
                    isInPatientContext ? navigate(-1) : navigate("/Task"))
                }
              />
            </>
          )}
        </Stack>
        <TaskFormConfirmationModal />
      </form>
    );
  },
);
